import {
  CreateStorageLocationPayload,
  MemberInvitations,
  RemoveMemberPayload,
} from '@luggagehero/dashboard-storage-locations-util';
import {
  ManageableStorageLocation,
  StorageLocationMemberInfo,
  StorageLocationPendingInvitation,
  StorageLocationQrCode,
} from '@luggagehero/shared/interfaces';
import { createAction, props } from '@ngrx/store';

import {
  CreateHotelsHeroStorageLocationPayload,
  CreateStorageAreaPayload,
  DuplicateStorageLocation,
  StorageLocationActivationStatus,
  StorageLocationPendingTaskUI,
} from './storage-locations.interfaces';

export const createHotelsHeroStorageLocation = createAction(
  '[StorageLocationsActions] Add Tax',
  props<{ payload: CreateHotelsHeroStorageLocationPayload }>(),
);
export const createHotelsHeroStorageLocationSuccess = createAction(
  '[StorageLocationsActions] Add Storage Location Success',
  props<{ storageLocation: ManageableStorageLocation }>(),
);
export const createHotelsHeroStorageLocationFailure = createAction(
  '[StorageLocationsActions] Add Storage Location Failure',
  props<{ error: unknown }>(),
);

export const createStorageLocation = createAction(
  '[StorageLocationsActions] Create Storage Location',
  props<{ payload: CreateStorageLocationPayload }>(),
);

export const createStorageLocationSuccess = createAction(
  '[StorageLocationsActions] Create Storage Location Success',
  props<{ storageLocation: ManageableStorageLocation }>(),
);
export const createStorageLocationFailure = createAction(
  '[StorageLocationsActions] Create Storage Location Failure',
  props<{ error: unknown }>(),
);

/**
 * AM-4752: the API refused the create because this organization already has a location at this
 * address, and named the one it found. Not a failure — the partner is about to be offered it.
 */
export const createStorageLocationDuplicate = createAction(
  '[StorageLocationsActions] Create Storage Location Duplicate Detected',
  props<{ existing: DuplicateStorageLocation }>(),
);

export const setSearchQuery = createAction(
  '[StorageLocationsActions] Set Search Query',
  props<{ searchQuery: string | undefined | null }>(),
);

export const loadStorageLocations = createAction('[StorageLocationsActions] Load Storage Locations');

export const loadStorageLocationsSuccess = createAction(
  '[StorageLocationsActions] Load Storage Locations Success',
  // AM-4723: `searchQuery` is the NORMALISED query this response answers (trimmed, '' for none).
  // Without it nothing knows which query the loaded list corresponds to, so a list left over from a
  // zero-result search counted as "loaded" for the cleared query too — an empty page that never
  // reloaded.
  props<{ storageLocations: ManageableStorageLocation[]; searchQuery: string }>(),
);
export const loadStorageLocationsFailure = createAction(
  '[StorageLocationsActions] Load Storage Locations Failure',
  props<{ error: unknown }>(),
);

export const loadMembers = createAction(
  '[StorageLocationsActions] Load Members',
  props<{ storageLocationId: string }>(),
);
export const loadMembersSuccess = createAction(
  '[StorageLocationsActions] Load Members Success',
  props<{ storageLocationId: string; members: StorageLocationMemberInfo[] }>(),
);
export const loadMembersFailure = createAction(
  '[StorageLocationsActions] Load Members Failure',
  props<{ error: unknown }>(),
);

export const loadPendingInvitations = createAction(
  '[StorageLocationsActions] Load Pending Invitations',
  props<{ storageLocationId: string }>(),
);
export const loadPendingInvitationsSuccess = createAction(
  '[StorageLocationsActions] Load Pending Invitations Success',
  props<{ storageLocationId: string; invitations: StorageLocationPendingInvitation[] }>(),
);
export const loadPendingInvitationsFailure = createAction(
  '[StorageLocationsActions] Load Pending Invitations Failure',
  props<{ error: unknown }>(),
);

export const cancelInvitation = createAction(
  '[StorageLocationsActions] Cancel Invitation',
  props<{ storageLocationId: string; token: string }>(),
);
export const cancelInvitationSuccess = createAction(
  '[StorageLocationsActions] Cancel Invitation Success',
  props<{ storageLocationId: string; token: string }>(),
);
export const cancelInvitationFailure = createAction(
  '[StorageLocationsActions] Cancel Invitation Failure',
  props<{ error: unknown }>(),
);

export const removeMember = createAction(
  '[StorageLocationsActions] Remove Member',
  props<{
    storageLocationId: string;
    payload: RemoveMemberPayload;
  }>(),
);

export const removeMemberSuccess = createAction(
  '[StorageLocationsActions] Remove Member Success',
  props<{ storageLocationId: string; email: string }>(),
);
export const removeMemberFailure = createAction(
  '[StorageLocationsActions] Remove Member Failure',
  props<{ error: unknown }>(),
);

export const attachMembers = createAction(
  '[StorageLocationsActions] Attach Members',
  props<{
    storageLocationId: string;
    payload: MemberInvitations[];
  }>(),
);
export const attachMembersSuccess = createAction(
  '[StorageLocationsActions] Attach Members Success',
  props<{ storageLocationId: string; payload: MemberInvitations[] }>(),
);
export const attachMembersFailure = createAction(
  '[StorageLocationsActions] Attach Members Failure',
  props<{ error: unknown }>(),
);

export const addStorageLocationArea = createAction(
  '[StorageLocationsActions] Add Storage Location Area',
  props<{ storageLocationId: string; payload: CreateStorageAreaPayload }>(),
);

export const addStorageLocationAreaSuccess = createAction(
  '[StorageLocationsActions] Add Storage Location Area Success',
  props<{ payload: unknown }>(),
);
export const addStorageLocationAreaFailure = createAction(
  '[StorageLocationsActions] Add Storage Location Area Failure',
  props<{ error: unknown }>(),
);

export const setSelectedStorageLocationById = createAction(
  '[StorageLocationsActions] Set Selected Storage Location By Id',
  props<{ id: string }>(),
);

export const loadStorageLocationById = createAction(
  '[StorageLocationsActions] Load Storage Location By Id',
  props<{ id: string }>(),
);

export const loadStorageLocationByIdSuccess = createAction(
  '[StorageLocationsActions] Load Storage Location By Id Success',
  props<{ storageLocation: ManageableStorageLocation }>(),
);
export const loadStorageLocationByIdFailure = createAction(
  '[StorageLocationsActions] Load Storage Location By Id Failure',
  props<{ error: unknown }>(),
);
export const setSelectedStorageLocations = createAction(
  '[StorageLocationsActions] Set Selected Storage Locations',
  props<{ storageLocations: string[] }>(),
);
export const resetSelectedStorageLocations = createAction('[StorageLocationsActions] Reset Selected Storage Locations');
export const reset = createAction('[StorageLocationsActions] Reset Storage Locations State');
export const updateStorageLocation = createAction(
  '[StorageLocationsActions] Update Storage Location',
  props<{ storageLocationId: string; storageLocation: Partial<ManageableStorageLocation> }>(),
);
export const updateStorageLocationSuccess = createAction(
  '[StorageLocationsActions] Update Storage Location Success',
  props<{ storageLocationId: string; storageLocation: ManageableStorageLocation }>(),
);
export const updateStorageLocationFailure = createAction(
  '[StorageLocationsActions] Update Storage Location Failure',
  props<{ storageLocationId: string; error: unknown }>(),
);
export const acceptPartnerProgramAgreement = createAction(
  '[StorageLocationsActions] Accept Partner Program Agreement',
  props<{ storageLocationId: string; agreementId: string }>(),
);
export const acceptPartnerProgramAgreementSuccess = createAction(
  '[StorageLocationsActions] Accept Partner Program Agreement Success',
  props<{ storageLocationId: string; success: boolean }>(),
);
export const acceptPartnerProgramAgreementFailure = createAction(
  '[StorageLocationsActions] Accept Partner Program Agreement Failure',
  props<{ storageLocationId: string; error: unknown }>(),
);

export const claimQrCode = createAction(
  '[StorageLocationsActions] Claim QR Code',
  props<{ storageLocationId: string; qrCode: string }>(),
);
export const claimQrCodeSuccess = createAction(
  '[StorageLocationsActions] Claim QR Code Success',
  props<{ storageLocationId: string; qrCode: string }>(),
);
export const claimQrCodeFailure = createAction(
  '[StorageLocationsActions] Claim QR Code Failure',
  props<{ error: unknown; qrCode: string }>(),
);

export const unclaimQrCode = createAction(
  '[StorageLocationsActions] Unclaim QR Code',
  props<{ storageLocationId: string; qrCode: StorageLocationQrCode }>(),
);
export const unclaimQrCodeSuccess = createAction(
  '[StorageLocationsActions] Unclaim QR Code Success',
  props<{ storageLocationId: string }>(),
);
export const unclaimQrCodeFailure = createAction(
  '[StorageLocationsActions] Unclaim QR Code Failure',
  props<{ error: unknown }>(),
);

export const deleteImage = createAction(
  '[StorageLocationsActions] Delete Image',
  props<{ storageLocationId: string; imageName: string }>(),
);

export const deleteImageSuccess = createAction(
  '[StorageLocationsActions] Delete Image Success',
  props<{ storageLocation: ManageableStorageLocation }>(),
);

export const deleteImageFailure = createAction(
  '[StorageLocationsActions] Delete Image Failure',
  props<{ error: unknown }>(),
);

export const loadStorageLocationPendingTasks = createAction(
  '[StorageLocationsActions] Load Storage Location Pending Tasks',
  props<{ storageLocationId: string }>(),
);

export const loadStorageLocationPendingTasksSuccess = createAction(
  '[StorageLocationsActions] Load Storage Location Pending Tasks Success',
  props<{
    storageLocationId: string;
    pendingTasks: StorageLocationPendingTaskUI[];
    activation: StorageLocationActivationStatus;
  }>(),
);

export const loadStorageLocationPendingTasksFailure = createAction(
  '[StorageLocationsActions] Load Storage Location Pending Tasks Failure',
  props<{ error: unknown }>(),
);

// AM-4710: the partner asking us to review the location and take it live. Returns the same
// checklist payload as the tasks endpoint, so success refreshes both the rows and the handshake.
export const requestStorageLocationReview = createAction(
  '[StorageLocationsActions] Request Storage Location Review',
  props<{ storageLocationId: string }>(),
);

export const requestStorageLocationReviewSuccess = createAction(
  '[StorageLocationsActions] Request Storage Location Review Success',
  props<{
    storageLocationId: string;
    pendingTasks: StorageLocationPendingTaskUI[];
    activation: StorageLocationActivationStatus;
  }>(),
);

export const requestStorageLocationReviewFailure = createAction(
  '[StorageLocationsActions] Request Storage Location Review Failure',
  props<{ storageLocationId: string; error: unknown }>(),
);

export const updateQrCodePrintedStatus = createAction(
  '[StorageLocationsActions] Update QR Code Printed Status',
  props<{ storageLocationId: string; printed: boolean }>(),
);

export const updateQrCodePrintedStatusSuccess = createAction(
  '[StorageLocationsActions] Update QR Code Printed Status Success',
  props<{ storageLocationId: string; printed: boolean }>(),
);

export const updateQrCodePrintedStatusFailure = createAction(
  '[StorageLocationsActions] Update QR Code Printed Status Failure',
  props<{ error: unknown }>(),
);

export const editShopAddress = createAction(
  '[StorageLocationsActions] Edit Shop Address',
  props<{
    storageLocationId: string;
    placeId?: string;
    address?: {
      street?: string;
      city?: string;
      postal?: string;
      country?: string;
      countryCode?: string;
      state?: string;
      stateLong?: string;
    };
  }>(),
);
export const editShopAddressSuccess = createAction(
  '[StorageLocationsActions] Edit Shop Address Success',
  props<{ storageLocationId: string; storageLocation: ManageableStorageLocation }>(),
);
export const editShopAddressFailure = createAction(
  '[StorageLocationsActions] Edit Shop Address Failure',
  props<{ error: unknown }>(),
);
